import React from "react";
import { drawerClasses, SwipeableDrawer } from "@mui/material";
import UserInfo from "./UserInfo";
import DistrictInfo from "./DistrictInfo";
import { useSidebarContext } from "../context/SidebarStateContext";
import { useMenuSelectionContext } from "../context/MenuSelectionContext";

/**
 * SidebarForMini Component
 * -------------------------
 * A collapsible sidebar for mini layouts.  
 * Renders optional sections dynamically: DistrictInfo, InviteCode, UserInfo, and any children passed.  
 * Uses Material-UI's SwipeableDrawer to allow swipe gestures for mobile devices.
 *
 * Props:
 * - showDistrictInfo: boolean → whether to render the DistrictInfo section
 * - showUserInfo: boolean → whether to render the UserInfo section
 * - children: optional React nodes → custom content passed inside the sidebar
 *
 * Workflow Diagram:
 *
 *   ┌───────────────────────────┐
 *   │ useMenuSelectionContext   │
 *   │  - open (drawer state)    │
 *   │  - setSelectedIndex       │────┐
 *   └───────────────────────────┘    │
 *                                    ▼
 *   ┌───────────────────────────┐
 *   │ useSidebarContext         │
 *   │  - isMainSidebar          │───────────────┐
 *   └───────────────────────────┘               │
 *                                               ▼
 *   ┌───────────────────────────────────────────────────────┐
 *   │ <SwipeableDrawer>                                     │
 *   │   • Styles (drawer width, left offset, transparent BG)│
 *   │   • onOpen → setSelectedIndex(11)                     │
 *   │   • onClose → setSelectedIndex(null)                  │
 *   │                                                       │
 *   │   ┌───────────────┐   ┌───────────────┐   ┌─────────┐ │
 *   │   │ DistrictInfo  │   │ children      │   │ UserInfo│ │
 *   │   │ (optional)    │→ │ (optional)    │→ │ (optional) │
 *   │   └───────────────┘   └───────────────┘   └─────────┘ │
 *   └───────────────────────────────────────────────────────┘
 *
 * Rendering order inside drawer:
 *   DistrictInfo → custom children → UserInfo
 */

const SidebarForMini = (props) => {
    const {
        showDistrictInfo = true,
        showUserInfo = false,
        children
    } = props;

    // Hook to manage which menu item is active
    const { setSelectedIndex, open } = useMenuSelectionContext();

    // Sidebar type: main sidebar vs district sidebar
    const { isMainSidebar } = useSidebarContext();

    return (
        <SwipeableDrawer
            anchor="left"                  // Sidebar slides from the left
            open={open}                     // Controls drawer open/close
            swipeAreaWidth={69}             // Defines swipeable edge width for gestures
            onClose={() => setSelectedIndex(null)} // Reset selected menu index on close
            onOpen={() => setSelectedIndex(11)}    // Set default menu index on open
            sx={(theme) => ({
                display: "block",
                width: theme.drawerWidth,
                left: theme.miniDrawerWidth,
                zIndex: theme.zIndex.drawer + 1,
                paddingTop: isMainSidebar ? 'inherit' : 7,
                [`& .${drawerClasses.paper}`]: {   // Drawer panel styles
                    left: theme.miniDrawerWidth,
                    width: theme.drawerWidth,
                    // backgroundColor: "#F9F9FA",
                    borderRight: "1px solid #DBDDE0",
                    paddingTop: 'inherit'
                },
                [`& .MuiModal-backdrop`]: {       // Transparent backdrop
                    backgroundColor: "transparent",
                },
            })}
        >
            {/* Optional: District section (collapsed menu mode enabled) */}
            {showDistrictInfo && (
                <DistrictInfo
                    // onAvatarClick={setSelectedIndex} // Clicking avatar changes menu index
                    isMenuCollapse={true}           
                />
            )}

            {/* Custom children injected by parent */}
            {children}

            {/* Optional: User info section (without avatar in mini mode) */}
            {showUserInfo && ( <UserInfo showAvtar={false} showDetail={true} /> )}
        </SwipeableDrawer>
    );
};

// Memoized for performance → re-renders only when props change
export default React.memo(SidebarForMini);
